import { Alert, AlertIcon, Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { FencyProvider } from "@fencyai/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import { type NeedDto, type NeedPatchDto } from "../../../autogen/bff-api";
import { useEditor } from "../../../common/editing/save-coordinator";
import { fencyPromise } from "../../../common/fency/fencySetup";
import { useFency } from "../../../common/fency/useFency";
import { useImproveNeedDescription } from "./useImproveNeedDescription";
import { useNeedTypeSuggestion } from "./useNeedTypeSuggestion";

const MODEL = "anthropic/claude-sonnet-4.6" as const;

export const NeedAiAssist = ({ needId, onDescriptionApplied }: { needId: string; onDescriptionApplied: () => void }) => {
  const { fetchCreateStreamClientToken, fetchCreateStructuredChatCompletionClientToken } = useFency();
  return (
    <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
      <NeedAiAssistInner
        needId={needId}
        fetchStructuredToken={fetchCreateStructuredChatCompletionClientToken}
        onDescriptionApplied={onDescriptionApplied}
      />
    </FencyProvider>
  );
};

type InnerProps = {
  needId: string;
  fetchStructuredToken: () => Promise<{ clientToken: string }>;
  onDescriptionApplied: () => void;
};

const NeedAiAssistInner = ({ needId, fetchStructuredToken, onDescriptionApplied }: InnerProps) => {
  const { t } = useTranslation();
  const { state, updateField } = useEditor<NeedDto, NeedPatchDto>(needId);
  // Both run as StructuredChatCompletion tasks, which require a CreateAgentTask session
  // (the structured-chat session) — not the CreateStream session FencyProvider takes.
  const improveDescription = useImproveNeedDescription({ model: MODEL, fetchClientToken: fetchStructuredToken });
  const typeSuggestion = useNeedTypeSuggestion({ model: MODEL, fetchClientToken: fetchStructuredToken });
  const [reasoning, setReasoning] = useState<string | null>(null);

  const title = state.title?.trim() ?? "";
  const description = useMemo(() => htmlToPlainText(state.description ?? ""), [state.description]);
  const hasContext = title !== "" || description !== "";

  const handleImprove = async () => {
    const improved = await improveDescription.improve(title, description);
    if (improved) {
      updateField({ description: improved });
      onDescriptionApplied();
    }
  };

  const handleSuggest = async () => {
    setReasoning(null);
    const suggestion = await typeSuggestion.suggest(title, description);
    if (!suggestion) return;
    updateField({ procurementType: suggestion.procurementType });
    if (!state.priority) updateField({ priority: suggestion.priority });
    setReasoning(suggestion.reasoning || null);
  };

  return (
    <Box border="1px solid" borderColor="smBorder" rounded="lg" p="4" mb="6" bg="smBackgroundSecondary">
      <Flex align="center" gap="2" mb="3">
        <Icon as={FaMagic} color="smPrimary" />
        <Text fontWeight="semibold" fontSize="sm" color="smText">
          {t("AI assistance")}
        </Text>
      </Flex>
      <Flex gap="3" wrap="wrap">
        <Button
          size="sm"
          variant="outline"
          colorScheme="teal"
          leftIcon={<Icon as={FaMagic} />}
          onClick={handleImprove}
          isLoading={improveDescription.isLoading}
          loadingText={t("Improving…")}
          isDisabled={!hasContext}
        >
          {t("Improve description with AI")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          colorScheme="teal"
          leftIcon={<Icon as={FaMagic} />}
          onClick={handleSuggest}
          isLoading={typeSuggestion.isLoading}
          loadingText={t("Analyzing…")}
          isDisabled={!hasContext}
        >
          {t("Suggest type & priority with AI")}
        </Button>
      </Flex>
      {!hasContext && (
        <Text mt="2" fontSize="xs" color="smMuted">
          {t("Add a title or description first")}
        </Text>
      )}
      {reasoning && (
        <Text mt="3" fontSize="sm" color="smMuted">
          {reasoning}
        </Text>
      )}
      {(improveDescription.hasError || typeSuggestion.hasError) && (
        <Alert status="error" mt="3" rounded="md" fontSize="sm">
          <AlertIcon />
          {t("Couldn't generate suggestions. Please try again.")}
        </Alert>
      )}
    </Box>
  );
};

const htmlToPlainText = (html: string): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
};
